// User guide content (EN / HI / PA). Sections are filtered by role.
const ALL = ['ADMIN', 'SUPERVISOR', 'OPERATOR'];
const MGR = ['ADMIN', 'SUPERVISOR'];
const ADM = ['ADMIN'];

export const SECTIONS = [
  { id: 'start', roles: ALL, icon: 'Rocket' },
  { id: 'install', roles: ALL, icon: 'Smartphone' },
  { id: 'terminal', roles: ALL, icon: 'Terminal' },
  { id: 'problems', roles: ALL, icon: 'AlertTriangle' },
  { id: 'setting', roles: ALL, icon: 'Timer' },
  { id: 'production', roles: ALL, icon: 'ClipboardCheck' },
  { id: 'material', roles: ALL, icon: 'Package' },
  { id: 'shift', roles: ALL, icon: 'Clock' },
  { id: 'notify', roles: ALL, icon: 'Bell' },
  { id: 'awards', roles: ALL, icon: 'Trophy' },
  { id: 'live', roles: MGR, icon: 'LayoutGrid' },
  { id: 'scheduling', roles: MGR, icon: 'CalendarClock' },
  { id: 'inventory', roles: MGR, icon: 'Boxes' },
  { id: 'assign', roles: MGR, icon: 'Users' },
  { id: 'reports', roles: MGR, icon: 'BarChart3' },
  { id: 'settings', roles: MGR, icon: 'Settings' },
  { id: 'users', roles: ADM, icon: 'UserCog' },
  { id: 'giveAwards', roles: ADM, icon: 'Gift' },
  { id: 'audit', roles: ADM, icon: 'History' },
  { id: 'faq', roles: ALL, icon: 'HelpCircle' },
];

export const GUIDE = {
  EN: {
    start: { title: 'Getting started', steps: [
      'Sign in with your email and password, or tap "Continue with Google". A new account stays "Pending" until an admin approves it and gives it a role.',
      'If asked, choose your factory. Everything you see stays inside that factory.',
      'Change the language at any time with the EN / हि / ਪੰ buttons. Your choice is saved to your account and follows you to every device.',
      'Open the menu (☰ on phone) to move between pages. Operators also have a quick bottom bar on phones.',
      'You stay signed in while you use the app. If you are away for a long time, the app asks you to sign in again — that is normal.',
    ] },
    install: { title: 'Install the app on your phone', steps: [
      'Android / Chrome: tap "Install app" on the banner, or open the browser menu → "Install app" / "Add to Home screen".',
      'iPhone / iPad: open the site in Safari, tap Share → "Add to Home Screen", then always open the app from that icon.',
      'The installed app opens full-screen and is needed for notifications when the app is closed (on iPhone it is required).',
    ] },
    terminal: { title: 'Operator Terminal — running your machine', steps: [
      'Open "Operator Terminal". Operators only see the machines assigned to them.',
      'Select the machine, then use the big buttons: Start (running), Stop, Break, Problem or Material Setting.',
      'The machine colour and status update live for everyone in the factory.',
      'If you pressed a button by mistake, use "Undo" within 60 seconds.',
      'Pick the current job from "Current Job" so your production is counted on the right order.',
    ] },
    problems: { title: 'Reporting a machine problem', steps: [
      'On the Terminal tap "Problem", choose the problem type (Mechanical, Electrical, Tooling…) and the severity.',
      'Pick a common reason or type your own. Your text is translated automatically for supervisors.',
      'A timer starts on your screen showing how long the machine has been down. It flashes red when it goes over the target time.',
      'Supervisors get a notification immediately. When fixed, press "Start" — the problem is closed and the downtime is recorded.',
      'See all tickets and their status (Open / In Progress / Resolved) on "Problems & Downtime".',
    ] },
    setting: { title: 'Material setting (changeover)', steps: [
      'When you change the raw material or set up the machine, tap "Material Setting" and choose the reason.',
      'A timer shows how long the setting is taking and turns red if it passes the target.',
      'Press "Start" when the machine runs again — the setting time is saved for reports and the leaderboard.',
    ] },
    production: { title: 'Logging production', steps: [
      'Use + and − in "Unit Counter" (or type the number) for the pieces made on the current job.',
      'Tap "Log Production" to save the count. Do this regularly during your shift.',
      'When the job is finished tap "Mark Complete". The machine becomes idle and supervisors are notified.',
    ] },
    material: { title: 'Using raw material', steps: [
      'In the "Raw material in use" card choose the material (it is auto-selected from the job when set).',
      'Type the quantity you used (decimals like 2.5 are allowed), add an optional note, and tap "Use (−)".',
      'You can also open "Materials" from the menu and tap "Use" next to any material.',
      'Operators can only subtract. Restocking is done by a supervisor or admin.',
      'Tap "View usage history" to see who used how much and when.',
    ] },
    shift: { title: 'Shifts, breaks and shift summary', steps: [
      'Your shift comes from your operator assignment.',
      'During the scheduled break, running machines pause automatically and resume after the break.',
      'At the end of your shift, running machines stop automatically. Machines with a problem or in material setting are left as they are.',
      'At shift end you see a summary: runtime, problems and changeovers for your shift.',
    ] },
    notify: { title: 'Phone notifications', steps: [
      'Operators: menu → "Notifications". Supervisors/Admins: "Settings". Turn on "Phone notifications" and tap "Allow".',
      'Choose what you want: Machine problems, Awards, Critical stock, Job completions, Schedule changes.',
      '"Mute outside my shift" silences notifications when you are off duty.',
      'Tap "Send test notification", then close the app — the test should arrive within seconds.',
      'If it fails, the app shows the reason. On iPhone, install the app to the Home Screen first.',
    ] },
    awards: { title: 'Awards and leaderboard', steps: [
      'Good work earns badges automatically and admins can give you personal awards and medals.',
      'When you receive an award you see a celebration on your screen. All your awards are in "My Awards".',
      '"Leaderboard" ranks operators by performance and fastest changeovers. Admins and supervisors are not ranked.',
    ] },
    live: { title: 'Live Machines dashboard', steps: [
      'Shows every machine with its live status, operator, current job and timers.',
      'Colours: green = running, red = problem, purple = material setting, blue = break, grey = idle/stopped.',
      'Use it on a big screen on the shop floor — it refreshes automatically.',
    ] },
    scheduling: { title: 'Production Scheduling', steps: [
      'Create a job: product, machine, quantity target (e.g. 0/500), start/end, shift and raw material.',
      'Change the status (Planned, In Progress, On Hold, Completed); the machine status follows the job.',
      'Operators then pick the job on their terminal and log production against it.',
    ] },
    inventory: { title: 'Materials and deliveries', steps: [
      'Materials: add items with stock, unit, reorder point and supplier. Status becomes LOW / CRITICAL automatically.',
      'Use "Reorder" on low items, or turn on auto-reorder in Settings to create draft deliveries automatically.',
      'Material Schedule: plan deliveries, approve drafts and mark them delivered.',
      'Every use and restock is logged — open the history icon and export it to CSV.',
    ] },
    assign: { title: 'Operators and machines', steps: [
      'Operator Assignments: assign each operator to machines and a shift. This controls what they see and when auto-stop happens.',
      'Machines: add or edit machines, lines and per-machine targets (changeover and downtime targets).',
    ] },
    reports: { title: 'Reports & Analytics', steps: [
      'Choose the period at the top: 1 Week, 1 Month, 6 Months, 12 Months or Custom dates.',
      'Filter by machine, or use "Compare" to compare two machines side by side.',
      'See OEE, output, downtime by reason, rejects, material consumption and changeover time.',
      'Export with "PDF" or "Export CSV". "Problem Report" gives a monthly problem analysis per machine.',
    ] },
    settings: { title: 'Settings', steps: [
      'Shift Timings: set each shift start/end and an optional break window.',
      'Regional: timezone, country, currency and downtime cost per hour.',
      'Shop-floor preferences, auto-reorder and your own notifications are also here.',
    ] },
    users: { title: 'User management (Admin)', steps: [
      'Approve new "Pending" users by giving them a role: Operator, Supervisor or Admin.',
      'Set which factory each user belongs to, reset passwords or remove users.',
      'Use the factory switcher in the menu to manage another factory.',
    ] },
    giveAwards: { title: 'Giving awards (Admin)', steps: [
      'Open "Give Awards", pick an operator, then use a template or design your own title, icon and colour.',
      'The operator sees a celebration instantly; supervisors are notified.',
    ] },
    audit: { title: 'Audit Trail', steps: [
      'Every change (who, what, when) is recorded. Filter by user or type to investigate.',
    ] },
    faq: { title: 'Common questions', steps: [
      'A button says "failed": check your internet, then sign out and sign in again.',
      'I don\'t see my machine: ask your supervisor to assign it to you in Operator Assignments.',
      'No notifications: open Notifications, turn it off and on, and tap "Send test notification".',
      'Need this guide again? Open "Help & Guide" from the menu at any time.',
    ] },
  },

  HI: {
    start: { title: 'शुरुआत करें', steps: [
      'अपने ईमेल और पासवर्ड से साइन इन करें, या "Continue with Google" दबाएं। नया खाता तब तक "Pending" रहता है जब तक एडमिन उसे मंज़ूरी देकर भूमिका न दे।',
      'पूछे जाने पर अपनी फैक्ट्री चुनें। आप जो भी देखते हैं वह उसी फैक्ट्री का होता है।',
      'EN / हि / ਪੰ बटन से कभी भी भाषा बदलें। आपकी पसंद आपके खाते में सेव होती है और हर डिवाइस पर लागू होती है।',
      'पेज बदलने के लिए मेनू (फ़ोन पर ☰) खोलें। ऑपरेटरों के लिए फ़ोन पर नीचे एक तेज़ बार भी है।',
      'ऐप इस्तेमाल करते समय आप साइन इन रहते हैं। लंबे समय बाद ऐप दोबारा साइन इन करने को कहता है — यह सामान्य है।',
    ] },
    install: { title: 'फ़ोन पर ऐप इंस्टॉल करें', steps: [
      'Android / Chrome: बैनर पर "ऐप इंस्टॉल करें" दबाएं, या ब्राउज़र मेनू → "Install app" / "Add to Home screen"।',
      'iPhone / iPad: Safari में साइट खोलें, शेयर → "Add to Home Screen" दबाएं, फिर हमेशा उसी आइकन से ऐप खोलें।',
      'इंस्टॉल किया ऐप फुल-स्क्रीन खुलता है और ऐप बंद होने पर सूचनाओं के लिए ज़रूरी है (iPhone पर अनिवार्य)।',
    ] },
    terminal: { title: 'ऑपरेटर टर्मिनल — मशीन चलाना', steps: [
      '"ऑपरेटर टर्मिनल" खोलें। ऑपरेटर केवल अपनी असाइन की गई मशीनें देखते हैं।',
      'मशीन चुनें, फिर बड़े बटन इस्तेमाल करें: Start (चालू), Stop, Break, Problem या Material Setting।',
      'मशीन का रंग और स्थिति पूरी फैक्ट्री में सभी के लिए तुरंत बदलती है।',
      'गलती से बटन दब गया हो तो 60 सेकंड के अंदर "Undo" दबाएं।',
      '"Current Job" से मौजूदा जॉब चुनें ताकि आपका उत्पादन सही ऑर्डर पर गिना जाए।',
    ] },
    problems: { title: 'मशीन की समस्या रिपोर्ट करना', steps: [
      'टर्मिनल पर "Problem" दबाएं, समस्या का प्रकार (मैकेनिकल, इलेक्ट्रिकल, टूलिंग…) और गंभीरता चुनें।',
      'आम कारण चुनें या अपना लिखें। आपका लिखा सुपरवाइज़र के लिए अपने-आप अनुवाद हो जाता है।',
      'आपकी स्क्रीन पर टाइमर चलता है कि मशीन कितनी देर से बंद है। लक्ष्य समय पार होने पर यह लाल चमकता है।',
      'सुपरवाइज़र को तुरंत सूचना मिलती है। ठीक होने पर "Start" दबाएं — समस्या बंद होती है और डाउनटाइम दर्ज होता है।',
      'सभी टिकट और उनकी स्थिति (Open / In Progress / Resolved) "समस्याएं और डाउनटाइम" में देखें।',
    ] },
    setting: { title: 'मटीरियल सेटिंग (चेंजओवर)', steps: [
      'कच्चा माल बदलते या मशीन सेट करते समय "Material Setting" दबाएं और कारण चुनें।',
      'टाइमर दिखाता है कि सेटिंग में कितना समय लग रहा है, लक्ष्य पार होने पर लाल हो जाता है।',
      'मशीन फिर चलने पर "Start" दबाएं — सेटिंग समय रिपोर्ट और लीडरबोर्ड के लिए सेव होता है।',
    ] },
    production: { title: 'उत्पादन दर्ज करना', steps: [
      '"Unit Counter" में + और − से (या नंबर लिखकर) मौजूदा जॉब पर बने पीस डालें।',
      'गिनती सेव करने के लिए "Log Production" दबाएं। शिफ्ट के दौरान ऐसा नियमित करें।',
      'जॉब पूरा होने पर "Mark Complete" दबाएं। मशीन idle हो जाती है और सुपरवाइज़र को सूचना जाती है।',
    ] },
    material: { title: 'कच्चे माल का उपयोग', steps: [
      '"Raw material in use" कार्ड में सामग्री चुनें (जॉब में सेट हो तो अपने-आप चुनी जाती है)।',
      'इस्तेमाल की गई मात्रा लिखें (2.5 जैसे दशमलव चलेंगे), चाहें तो नोट जोड़ें, और "Use (−)" दबाएं।',
      'आप मेनू से "सामग्री" खोलकर किसी भी सामग्री के आगे "उपयोग" भी दबा सकते हैं।',
      'ऑपरेटर केवल घटा सकते हैं। रीस्टॉक सुपरवाइज़र या एडमिन करते हैं।',
      '"View usage history" से देखें किसने कितना और कब इस्तेमाल किया।',
    ] },
    shift: { title: 'शिफ्ट, ब्रेक और शिफ्ट सारांश', steps: [
      'आपकी शिफ्ट आपके ऑपरेटर असाइनमेंट से तय होती है।',
      'तय ब्रेक के दौरान चलती मशीनें अपने-आप रुकती हैं और ब्रेक के बाद फिर चालू होती हैं।',
      'शिफ्ट खत्म होने पर चलती मशीनें अपने-आप बंद होती हैं। समस्या या मटीरियल सेटिंग वाली मशीनें वैसी ही रहती हैं।',
      'शिफ्ट के अंत में सारांश दिखता है: रनटाइम, समस्याएं और चेंजओवर।',
    ] },
    notify: { title: 'फ़ोन सूचनाएं', steps: [
      'ऑपरेटर: मेनू → "सूचनाएं"। सुपरवाइज़र/एडमिन: "सेटिंग्स"। "फ़ोन सूचनाएं" चालू करें और "Allow" दबाएं।',
      'चुनें क्या चाहिए: मशीन समस्याएं, पुरस्कार, क्रिटिकल स्टॉक, जॉब पूरे होना, शेड्यूल बदलाव।',
      '"मेरी शिफ्ट के बाहर म्यूट करें" ड्यूटी के बाहर सूचनाएं बंद रखता है।',
      '"टेस्ट सूचना भेजें" दबाएं, फिर ऐप बंद करें — कुछ सेकंड में टेस्ट आनी चाहिए।',
      'विफल होने पर ऐप कारण बताता है। iPhone पर पहले ऐप को होम स्क्रीन पर इंस्टॉल करें।',
    ] },
    awards: { title: 'पुरस्कार और लीडरबोर्ड', steps: [
      'अच्छे काम पर बैज अपने-आप मिलते हैं और एडमिन आपको विशेष पुरस्कार व मेडल दे सकते हैं।',
      'पुरस्कार मिलने पर स्क्रीन पर जश्न दिखता है। आपके सभी पुरस्कार "मेरे पुरस्कार" में हैं।',
      '"लीडरबोर्ड" ऑपरेटरों को प्रदर्शन और सबसे तेज़ चेंजओवर पर रैंक करता है। एडमिन व सुपरवाइज़र रैंक नहीं होते।',
    ] },
    live: { title: 'लाइव मशीनें डैशबोर्ड', steps: [
      'हर मशीन की लाइव स्थिति, ऑपरेटर, मौजूदा जॉब और टाइमर दिखाता है।',
      'रंग: हरा = चालू, लाल = समस्या, बैंगनी = मटीरियल सेटिंग, नीला = ब्रेक, स्लेटी = idle/बंद।',
      'शॉप फ्लोर पर बड़ी स्क्रीन पर लगाएं — यह अपने-आप रिफ्रेश होता है।',
    ] },
    scheduling: { title: 'उत्पादन शेड्यूलिंग', steps: [
      'जॉब बनाएं: प्रोडक्ट, मशीन, मात्रा लक्ष्य (जैसे 0/500), शुरू/खत्म, शिफ्ट और कच्चा माल।',
      'स्थिति बदलें (Planned, In Progress, On Hold, Completed); मशीन की स्थिति जॉब के अनुसार बदलती है।',
      'फिर ऑपरेटर टर्मिनल पर जॉब चुनकर उस पर उत्पादन दर्ज करते हैं।',
    ] },
    inventory: { title: 'सामग्री और डिलीवरी', steps: [
      'सामग्री: स्टॉक, यूनिट, रीऑर्डर पॉइंट और सप्लायर के साथ आइटम जोड़ें। स्थिति LOW / CRITICAL अपने-आप बनती है।',
      'कम आइटम पर "Reorder" दबाएं, या सेटिंग्स में ऑटो-रीऑर्डर चालू करें।',
      'सामग्री शेड्यूल: डिलीवरी प्लान करें, ड्राफ्ट मंज़ूर करें और डिलीवर्ड मार्क करें।',
      'हर उपयोग और रीस्टॉक दर्ज होता है — हिस्ट्री आइकन खोलें और CSV में एक्सपोर्ट करें।',
    ] },
    assign: { title: 'ऑपरेटर और मशीनें', steps: [
      'ऑपरेटर असाइनमेंट: हर ऑपरेटर को मशीनें और शिफ्ट दें। इससे तय होता है वे क्या देखते हैं और ऑटो-स्टॉप कब होता है।',
      'मशीनें: मशीनें, लाइनें और हर मशीन के लक्ष्य (चेंजओवर व डाउनटाइम) जोड़ें या बदलें।',
    ] },
    reports: { title: 'रिपोर्ट और विश्लेषण', steps: [
      'ऊपर अवधि चुनें: 1 सप्ताह, 1 महीना, 6 महीने, 12 महीने या कस्टम तारीखें।',
      'मशीन से फ़िल्टर करें, या "Compare" से दो मशीनों की तुलना करें।',
      'OEE, उत्पादन, कारण अनुसार डाउनटाइम, रिजेक्ट, सामग्री खपत और चेंजओवर समय देखें।',
      '"PDF" या "Export CSV" से एक्सपोर्ट करें। "समस्या रिपोर्ट" हर मशीन का मासिक विश्लेषण देती है।',
    ] },
    settings: { title: 'सेटिंग्स', steps: [
      'शिफ्ट समय: हर शिफ्ट का शुरू/खत्म समय और वैकल्पिक ब्रेक समय सेट करें।',
      'क्षेत्रीय: टाइमज़ोन, देश, मुद्रा और प्रति घंटा डाउनटाइम लागत।',
      'शॉप-फ्लोर पसंद, ऑटो-रीऑर्डर और आपकी सूचनाएं भी यहीं हैं।',
    ] },
    users: { title: 'यूज़र प्रबंधन (एडमिन)', steps: [
      'नए "Pending" यूज़र को भूमिका देकर मंज़ूरी दें: ऑपरेटर, सुपरवाइज़र या एडमिन।',
      'हर यूज़र की फैक्ट्री सेट करें, पासवर्ड रीसेट करें या यूज़र हटाएं।',
      'दूसरी फैक्ट्री संभालने के लिए मेनू में फैक्ट्री स्विचर इस्तेमाल करें।',
    ] },
    giveAwards: { title: 'पुरस्कार देना (एडमिन)', steps: [
      '"पुरस्कार दें" खोलें, ऑपरेटर चुनें, फिर टेम्पलेट लें या अपना शीर्षक, आइकन और रंग बनाएं।',
      'ऑपरेटर को तुरंत जश्न दिखता है; सुपरवाइज़र को सूचना जाती है।',
    ] },
    audit: { title: 'ऑडिट ट्रेल', steps: [
      'हर बदलाव (किसने, क्या, कब) दर्ज होता है। जांच के लिए यूज़र या प्रकार से फ़िल्टर करें।',
    ] },
    faq: { title: 'आम सवाल', steps: [
      'बटन "failed" दिखाए: इंटरनेट जांचें, फिर साइन आउट करके दोबारा साइन इन करें।',
      'मेरी मशीन नहीं दिख रही: सुपरवाइज़र से ऑपरेटर असाइनमेंट में मशीन असाइन करवाएं।',
      'सूचनाएं नहीं आ रहीं: सूचनाएं खोलें, बंद करके फिर चालू करें और "टेस्ट सूचना भेजें" दबाएं।',
      'यह गाइड फिर चाहिए? मेनू से कभी भी "मदद और गाइड" खोलें।',
    ] },
  },

  PA: {
    start: { title: 'ਸ਼ੁਰੂਆਤ ਕਰੋ', steps: [
      'ਆਪਣੀ ਈਮੇਲ ਅਤੇ ਪਾਸਵਰਡ ਨਾਲ ਸਾਈਨ ਇਨ ਕਰੋ, ਜਾਂ "Continue with Google" ਦਬਾਓ। ਨਵਾਂ ਖਾਤਾ ਉਦੋਂ ਤੱਕ "Pending" ਰਹਿੰਦਾ ਹੈ ਜਦੋਂ ਤੱਕ ਐਡਮਿਨ ਮਨਜ਼ੂਰੀ ਦੇ ਕੇ ਭੂਮਿਕਾ ਨਾ ਦੇਵੇ।',
      'ਪੁੱਛੇ ਜਾਣ ਤੇ ਆਪਣੀ ਫੈਕਟਰੀ ਚੁਣੋ। ਤੁਸੀਂ ਜੋ ਵੀ ਵੇਖਦੇ ਹੋ ਉਹ ਉਸੇ ਫੈਕਟਰੀ ਦਾ ਹੁੰਦਾ ਹੈ।',
      'EN / हि / ਪੰ ਬਟਨਾਂ ਨਾਲ ਕਦੇ ਵੀ ਭਾਸ਼ਾ ਬਦਲੋ। ਤੁਹਾਡੀ ਚੋਣ ਖਾਤੇ ਵਿੱਚ ਸੇਵ ਹੁੰਦੀ ਹੈ ਅਤੇ ਹਰ ਡਿਵਾਈਸ ਤੇ ਲਾਗੂ ਹੁੰਦੀ ਹੈ।',
      'ਪੇਜ ਬਦਲਣ ਲਈ ਮੀਨੂ (ਫ਼ੋਨ ਤੇ ☰) ਖੋਲ੍ਹੋ। ਓਪਰੇਟਰਾਂ ਲਈ ਫ਼ੋਨ ਤੇ ਹੇਠਾਂ ਇੱਕ ਤੇਜ਼ ਬਾਰ ਵੀ ਹੈ।',
      'ਐਪ ਵਰਤਦੇ ਸਮੇਂ ਤੁਸੀਂ ਸਾਈਨ ਇਨ ਰਹਿੰਦੇ ਹੋ। ਲੰਮੇ ਸਮੇਂ ਬਾਅਦ ਐਪ ਦੁਬਾਰਾ ਸਾਈਨ ਇਨ ਕਰਨ ਨੂੰ ਕਹਿੰਦਾ ਹੈ — ਇਹ ਆਮ ਹੈ।',
    ] },
    install: { title: 'ਫ਼ੋਨ ਤੇ ਐਪ ਇੰਸਟਾਲ ਕਰੋ', steps: [
      'Android / Chrome: ਬੈਨਰ ਤੇ "ਐਪ ਇੰਸਟਾਲ ਕਰੋ" ਦਬਾਓ, ਜਾਂ ਬ੍ਰਾਊਜ਼ਰ ਮੀਨੂ → "Install app" / "Add to Home screen"।',
      'iPhone / iPad: Safari ਵਿੱਚ ਸਾਈਟ ਖੋਲ੍ਹੋ, ਸ਼ੇਅਰ → "Add to Home Screen" ਦਬਾਓ, ਫਿਰ ਹਮੇਸ਼ਾ ਉਸੇ ਆਈਕਨ ਤੋਂ ਐਪ ਖੋਲ੍ਹੋ।',
      'ਇੰਸਟਾਲ ਐਪ ਪੂਰੀ ਸਕ੍ਰੀਨ ਤੇ ਖੁੱਲ੍ਹਦਾ ਹੈ ਅਤੇ ਐਪ ਬੰਦ ਹੋਣ ਤੇ ਸੂਚਨਾਵਾਂ ਲਈ ਲੋੜੀਂਦਾ ਹੈ (iPhone ਤੇ ਜ਼ਰੂਰੀ)।',
    ] },
    terminal: { title: 'ਓਪਰੇਟਰ ਟਰਮੀਨਲ — ਮਸ਼ੀਨ ਚਲਾਉਣਾ', steps: [
      '"ਓਪਰੇਟਰ ਟਰਮੀਨਲ" ਖੋਲ੍ਹੋ। ਓਪਰੇਟਰ ਸਿਰਫ਼ ਆਪਣੀਆਂ ਨਿਰਧਾਰਤ ਮਸ਼ੀਨਾਂ ਵੇਖਦੇ ਹਨ।',
      'ਮਸ਼ੀਨ ਚੁਣੋ, ਫਿਰ ਵੱਡੇ ਬਟਨ ਵਰਤੋ: Start (ਚਾਲੂ), Stop, Break, Problem ਜਾਂ Material Setting।',
      'ਮਸ਼ੀਨ ਦਾ ਰੰਗ ਅਤੇ ਸਥਿਤੀ ਪੂਰੀ ਫੈਕਟਰੀ ਵਿੱਚ ਸਾਰਿਆਂ ਲਈ ਤੁਰੰਤ ਬਦਲਦੀ ਹੈ।',
      'ਗਲਤੀ ਨਾਲ ਬਟਨ ਦੱਬ ਗਿਆ ਹੋਵੇ ਤਾਂ 60 ਸਕਿੰਟਾਂ ਦੇ ਅੰਦਰ "Undo" ਦਬਾਓ।',
      '"Current Job" ਤੋਂ ਮੌਜੂਦਾ ਜੌਬ ਚੁਣੋ ਤਾਂ ਜੋ ਤੁਹਾਡਾ ਉਤਪਾਦਨ ਸਹੀ ਆਰਡਰ ਤੇ ਗਿਣਿਆ ਜਾਵੇ।',
    ] },
    problems: { title: 'ਮਸ਼ੀਨ ਦੀ ਸਮੱਸਿਆ ਰਿਪੋਰਟ ਕਰਨਾ', steps: [
      'ਟਰਮੀਨਲ ਤੇ "Problem" ਦਬਾਓ, ਸਮੱਸਿਆ ਦੀ ਕਿਸਮ (ਮਕੈਨੀਕਲ, ਇਲੈਕਟ੍ਰੀਕਲ, ਟੂਲਿੰਗ…) ਅਤੇ ਗੰਭੀਰਤਾ ਚੁਣੋ।',
      'ਆਮ ਕਾਰਨ ਚੁਣੋ ਜਾਂ ਆਪਣਾ ਲਿਖੋ। ਤੁਹਾਡਾ ਲਿਖਿਆ ਸੁਪਰਵਾਈਜ਼ਰ ਲਈ ਆਪਣੇ-ਆਪ ਅਨੁਵਾਦ ਹੋ ਜਾਂਦਾ ਹੈ।',
      'ਤੁਹਾਡੀ ਸਕ੍ਰੀਨ ਤੇ ਟਾਈਮਰ ਚੱਲਦਾ ਹੈ ਕਿ ਮਸ਼ੀਨ ਕਿੰਨੇ ਸਮੇਂ ਤੋਂ ਬੰਦ ਹੈ। ਟੀਚਾ ਸਮਾਂ ਪਾਰ ਹੋਣ ਤੇ ਇਹ ਲਾਲ ਚਮਕਦਾ ਹੈ।',
      'ਸੁਪਰਵਾਈਜ਼ਰ ਨੂੰ ਤੁਰੰਤ ਸੂਚਨਾ ਮਿਲਦੀ ਹੈ। ਠੀਕ ਹੋਣ ਤੇ "Start" ਦਬਾਓ — ਸਮੱਸਿਆ ਬੰਦ ਹੁੰਦੀ ਹੈ ਅਤੇ ਡਾਊਨਟਾਈਮ ਦਰਜ ਹੁੰਦਾ ਹੈ।',
      'ਸਾਰੀਆਂ ਟਿਕਟਾਂ ਅਤੇ ਉਨ੍ਹਾਂ ਦੀ ਸਥਿਤੀ (Open / In Progress / Resolved) "ਸਮੱਸਿਆਵਾਂ ਤੇ ਡਾਊਨਟਾਈਮ" ਵਿੱਚ ਵੇਖੋ।',
    ] },
    setting: { title: 'ਮਟੀਰੀਅਲ ਸੈਟਿੰਗ (ਚੇਂਜਓਵਰ)', steps: [
      'ਕੱਚਾ ਮਾਲ ਬਦਲਦੇ ਜਾਂ ਮਸ਼ੀਨ ਸੈੱਟ ਕਰਦੇ ਸਮੇਂ "Material Setting" ਦਬਾਓ ਅਤੇ ਕਾਰਨ ਚੁਣੋ।',
      'ਟਾਈਮਰ ਦੱਸਦਾ ਹੈ ਕਿ ਸੈਟਿੰਗ ਵਿੱਚ ਕਿੰਨਾ ਸਮਾਂ ਲੱਗ ਰਿਹਾ ਹੈ, ਟੀਚਾ ਪਾਰ ਹੋਣ ਤੇ ਲਾਲ ਹੋ ਜਾਂਦਾ ਹੈ।',
      'ਮਸ਼ੀਨ ਫਿਰ ਚੱਲਣ ਤੇ "Start" ਦਬਾਓ — ਸੈਟਿੰਗ ਸਮਾਂ ਰਿਪੋਰਟ ਅਤੇ ਲੀਡਰਬੋਰਡ ਲਈ ਸੇਵ ਹੁੰਦਾ ਹੈ।',
    ] },
    production: { title: 'ਉਤਪਾਦਨ ਦਰਜ ਕਰਨਾ', steps: [
      '"Unit Counter" ਵਿੱਚ + ਅਤੇ − ਨਾਲ (ਜਾਂ ਨੰਬਰ ਲਿਖ ਕੇ) ਮੌਜੂਦਾ ਜੌਬ ਤੇ ਬਣੇ ਪੀਸ ਪਾਓ।',
      'ਗਿਣਤੀ ਸੇਵ ਕਰਨ ਲਈ "Log Production" ਦਬਾਓ। ਸ਼ਿਫਟ ਦੌਰਾਨ ਅਜਿਹਾ ਨਿਯਮਿਤ ਕਰੋ।',
      'ਜੌਬ ਪੂਰਾ ਹੋਣ ਤੇ "Mark Complete" ਦਬਾਓ। ਮਸ਼ੀਨ idle ਹੋ ਜਾਂਦੀ ਹੈ ਅਤੇ ਸੁਪਰਵਾਈਜ਼ਰ ਨੂੰ ਸੂਚਨਾ ਜਾਂਦੀ ਹੈ।',
    ] },
    material: { title: 'ਕੱਚੇ ਮਾਲ ਦੀ ਵਰਤੋਂ', steps: [
      '"Raw material in use" ਕਾਰਡ ਵਿੱਚ ਸਮੱਗਰੀ ਚੁਣੋ (ਜੌਬ ਵਿੱਚ ਸੈੱਟ ਹੋਵੇ ਤਾਂ ਆਪਣੇ-ਆਪ ਚੁਣੀ ਜਾਂਦੀ ਹੈ)।',
      'ਵਰਤੀ ਮਾਤਰਾ ਲਿਖੋ (2.5 ਵਰਗੇ ਦਸ਼ਮਲਵ ਚੱਲਣਗੇ), ਚਾਹੋ ਤਾਂ ਨੋਟ ਜੋੜੋ, ਅਤੇ "Use (−)" ਦਬਾਓ।',
      'ਤੁਸੀਂ ਮੀਨੂ ਤੋਂ "ਸਮੱਗਰੀ" ਖੋਲ੍ਹ ਕੇ ਕਿਸੇ ਵੀ ਸਮੱਗਰੀ ਅੱਗੇ "ਵਰਤੋਂ" ਵੀ ਦਬਾ ਸਕਦੇ ਹੋ।',
      'ਓਪਰੇਟਰ ਸਿਰਫ਼ ਘਟਾ ਸਕਦੇ ਹਨ। ਰੀਸਟਾਕ ਸੁਪਰਵਾਈਜ਼ਰ ਜਾਂ ਐਡਮਿਨ ਕਰਦੇ ਹਨ।',
      '"View usage history" ਨਾਲ ਵੇਖੋ ਕਿਸ ਨੇ ਕਿੰਨਾ ਅਤੇ ਕਦੋਂ ਵਰਤਿਆ।',
    ] },
    shift: { title: 'ਸ਼ਿਫਟ, ਬ੍ਰੇਕ ਅਤੇ ਸ਼ਿਫਟ ਸਾਰ', steps: [
      'ਤੁਹਾਡੀ ਸ਼ਿਫਟ ਤੁਹਾਡੇ ਓਪਰੇਟਰ ਅਸਾਈਨਮੈਂਟ ਤੋਂ ਤੈਅ ਹੁੰਦੀ ਹੈ।',
      'ਤੈਅ ਬ੍ਰੇਕ ਦੌਰਾਨ ਚੱਲਦੀਆਂ ਮਸ਼ੀਨਾਂ ਆਪਣੇ-ਆਪ ਰੁਕਦੀਆਂ ਹਨ ਅਤੇ ਬ੍ਰੇਕ ਤੋਂ ਬਾਅਦ ਫਿਰ ਚਾਲੂ ਹੁੰਦੀਆਂ ਹਨ।',
      'ਸ਼ਿਫਟ ਖਤਮ ਹੋਣ ਤੇ ਚੱਲਦੀਆਂ ਮਸ਼ੀਨਾਂ ਆਪਣੇ-ਆਪ ਬੰਦ ਹੁੰਦੀਆਂ ਹਨ। ਸਮੱਸਿਆ ਜਾਂ ਮਟੀਰੀਅਲ ਸੈਟਿੰਗ ਵਾਲੀਆਂ ਮਸ਼ੀਨਾਂ ਉਵੇਂ ਹੀ ਰਹਿੰਦੀਆਂ ਹਨ।',
      'ਸ਼ਿਫਟ ਦੇ ਅੰਤ ਤੇ ਸਾਰ ਦਿਸਦਾ ਹੈ: ਰਨਟਾਈਮ, ਸਮੱਸਿਆਵਾਂ ਅਤੇ ਚੇਂਜਓਵਰ।',
    ] },
    notify: { title: 'ਫ਼ੋਨ ਸੂਚਨਾਵਾਂ', steps: [
      'ਓਪਰੇਟਰ: ਮੀਨੂ → "ਸੂਚਨਾਵਾਂ"। ਸੁਪਰਵਾਈਜ਼ਰ/ਐਡਮਿਨ: "ਸੈਟਿੰਗਾਂ"। "ਫ਼ੋਨ ਸੂਚਨਾਵਾਂ" ਚਾਲੂ ਕਰੋ ਅਤੇ "Allow" ਦਬਾਓ।',
      'ਚੁਣੋ ਕੀ ਚਾਹੀਦਾ ਹੈ: ਮਸ਼ੀਨ ਸਮੱਸਿਆਵਾਂ, ਇਨਾਮ, ਕ੍ਰਿਟੀਕਲ ਸਟਾਕ, ਜੌਬ ਪੂਰੇ ਹੋਣਾ, ਸ਼ਡਿਊਲ ਬਦਲਾਅ।',
      '"ਮੇਰੀ ਸ਼ਿਫਟ ਤੋਂ ਬਾਹਰ ਮਿਊਟ ਕਰੋ" ਡਿਊਟੀ ਤੋਂ ਬਾਹਰ ਸੂਚਨਾਵਾਂ ਬੰਦ ਰੱਖਦਾ ਹੈ।',
      '"ਟੈਸਟ ਸੂਚਨਾ ਭੇਜੋ" ਦਬਾਓ, ਫਿਰ ਐਪ ਬੰਦ ਕਰੋ — ਕੁਝ ਸਕਿੰਟਾਂ ਵਿੱਚ ਟੈਸਟ ਆਉਣੀ ਚਾਹੀਦੀ ਹੈ।',
      'ਫੇਲ੍ਹ ਹੋਣ ਤੇ ਐਪ ਕਾਰਨ ਦੱਸਦਾ ਹੈ। iPhone ਤੇ ਪਹਿਲਾਂ ਐਪ ਨੂੰ ਹੋਮ ਸਕ੍ਰੀਨ ਤੇ ਇੰਸਟਾਲ ਕਰੋ।',
    ] },
    awards: { title: 'ਇਨਾਮ ਅਤੇ ਲੀਡਰਬੋਰਡ', steps: [
      'ਚੰਗੇ ਕੰਮ ਤੇ ਬੈਜ ਆਪਣੇ-ਆਪ ਮਿਲਦੇ ਹਨ ਅਤੇ ਐਡਮਿਨ ਤੁਹਾਨੂੰ ਖਾਸ ਇਨਾਮ ਤੇ ਮੈਡਲ ਦੇ ਸਕਦੇ ਹਨ।',
      'ਇਨਾਮ ਮਿਲਣ ਤੇ ਸਕ੍ਰੀਨ ਤੇ ਜਸ਼ਨ ਦਿਸਦਾ ਹੈ। ਤੁਹਾਡੇ ਸਾਰੇ ਇਨਾਮ "ਮੇਰੇ ਇਨਾਮ" ਵਿੱਚ ਹਨ।',
      '"ਲੀਡਰਬੋਰਡ" ਓਪਰੇਟਰਾਂ ਨੂੰ ਕਾਰਗੁਜ਼ਾਰੀ ਅਤੇ ਸਭ ਤੋਂ ਤੇਜ਼ ਚੇਂਜਓਵਰ ਤੇ ਰੈਂਕ ਕਰਦਾ ਹੈ। ਐਡਮਿਨ ਤੇ ਸੁਪਰਵਾਈਜ਼ਰ ਰੈਂਕ ਨਹੀਂ ਹੁੰਦੇ।',
    ] },
    live: { title: 'ਲਾਈਵ ਮਸ਼ੀਨਾਂ ਡੈਸ਼ਬੋਰਡ', steps: [
      'ਹਰ ਮਸ਼ੀਨ ਦੀ ਲਾਈਵ ਸਥਿਤੀ, ਓਪਰੇਟਰ, ਮੌਜੂਦਾ ਜੌਬ ਅਤੇ ਟਾਈਮਰ ਦਿਖਾਉਂਦਾ ਹੈ।',
      'ਰੰਗ: ਹਰਾ = ਚਾਲੂ, ਲਾਲ = ਸਮੱਸਿਆ, ਜਾਮਨੀ = ਮਟੀਰੀਅਲ ਸੈਟਿੰਗ, ਨੀਲਾ = ਬ੍ਰੇਕ, ਸਲੇਟੀ = idle/ਬੰਦ।',
      'ਸ਼ਾਪ ਫਲੋਰ ਤੇ ਵੱਡੀ ਸਕ੍ਰੀਨ ਤੇ ਲਗਾਓ — ਇਹ ਆਪਣੇ-ਆਪ ਰਿਫ੍ਰੈਸ਼ ਹੁੰਦਾ ਹੈ।',
    ] },
    scheduling: { title: 'ਉਤਪਾਦਨ ਸ਼ਡਿਊਲਿੰਗ', steps: [
      'ਜੌਬ ਬਣਾਓ: ਪ੍ਰੋਡਕਟ, ਮਸ਼ੀਨ, ਮਾਤਰਾ ਟੀਚਾ (ਜਿਵੇਂ 0/500), ਸ਼ੁਰੂ/ਖਤਮ, ਸ਼ਿਫਟ ਅਤੇ ਕੱਚਾ ਮਾਲ।',
      'ਸਥਿਤੀ ਬਦਲੋ (Planned, In Progress, On Hold, Completed); ਮਸ਼ੀਨ ਦੀ ਸਥਿਤੀ ਜੌਬ ਅਨੁਸਾਰ ਬਦਲਦੀ ਹੈ।',
      'ਫਿਰ ਓਪਰੇਟਰ ਟਰਮੀਨਲ ਤੇ ਜੌਬ ਚੁਣ ਕੇ ਉਸ ਤੇ ਉਤਪਾਦਨ ਦਰਜ ਕਰਦੇ ਹਨ।',
    ] },
    inventory: { title: 'ਸਮੱਗਰੀ ਅਤੇ ਡਿਲੀਵਰੀ', steps: [
      'ਸਮੱਗਰੀ: ਸਟਾਕ, ਯੂਨਿਟ, ਰੀਆਰਡਰ ਪੁਆਇੰਟ ਅਤੇ ਸਪਲਾਇਰ ਨਾਲ ਆਈਟਮ ਜੋੜੋ। ਸਥਿਤੀ LOW / CRITICAL ਆਪਣੇ-ਆਪ ਬਣਦੀ ਹੈ।',
      'ਘੱਟ ਆਈਟਮ ਤੇ "Reorder" ਦਬਾਓ, ਜਾਂ ਸੈਟਿੰਗਾਂ ਵਿੱਚ ਆਟੋ-ਰੀਆਰਡਰ ਚਾਲੂ ਕਰੋ।',
      'ਸਮੱਗਰੀ ਸ਼ਡਿਊਲ: ਡਿਲੀਵਰੀ ਪਲਾਨ ਕਰੋ, ਡਰਾਫਟ ਮਨਜ਼ੂਰ ਕਰੋ ਅਤੇ ਡਿਲੀਵਰਡ ਮਾਰਕ ਕਰੋ।',
      'ਹਰ ਵਰਤੋਂ ਅਤੇ ਰੀਸਟਾਕ ਦਰਜ ਹੁੰਦਾ ਹੈ — ਹਿਸਟਰੀ ਆਈਕਨ ਖੋਲ੍ਹੋ ਅਤੇ CSV ਵਿੱਚ ਐਕਸਪੋਰਟ ਕਰੋ।',
    ] },
    assign: { title: 'ਓਪਰੇਟਰ ਅਤੇ ਮਸ਼ੀਨਾਂ', steps: [
      'ਓਪਰੇਟਰ ਅਸਾਈਨਮੈਂਟ: ਹਰ ਓਪਰੇਟਰ ਨੂੰ ਮਸ਼ੀਨਾਂ ਅਤੇ ਸ਼ਿਫਟ ਦਿਓ। ਇਸ ਨਾਲ ਤੈਅ ਹੁੰਦਾ ਹੈ ਉਹ ਕੀ ਵੇਖਦੇ ਹਨ ਅਤੇ ਆਟੋ-ਸਟਾਪ ਕਦੋਂ ਹੁੰਦਾ ਹੈ।',
      'ਮਸ਼ੀਨਾਂ: ਮਸ਼ੀਨਾਂ, ਲਾਈਨਾਂ ਅਤੇ ਹਰ ਮਸ਼ੀਨ ਦੇ ਟੀਚੇ (ਚੇਂਜਓਵਰ ਤੇ ਡਾਊਨਟਾਈਮ) ਜੋੜੋ ਜਾਂ ਬਦਲੋ।',
    ] },
    reports: { title: 'ਰਿਪੋਰਟ ਤੇ ਵਿਸ਼ਲੇਸ਼ਣ', steps: [
      'ਉੱਪਰ ਮਿਆਦ ਚੁਣੋ: 1 ਹਫ਼ਤਾ, 1 ਮਹੀਨਾ, 6 ਮਹੀਨੇ, 12 ਮਹੀਨੇ ਜਾਂ ਕਸਟਮ ਤਾਰੀਖਾਂ।',
      'ਮਸ਼ੀਨ ਨਾਲ ਫ਼ਿਲਟਰ ਕਰੋ, ਜਾਂ "Compare" ਨਾਲ ਦੋ ਮਸ਼ੀਨਾਂ ਦੀ ਤੁਲਨਾ ਕਰੋ।',
      'OEE, ਉਤਪਾਦਨ, ਕਾਰਨ ਅਨੁਸਾਰ ਡਾਊਨਟਾਈਮ, ਰਿਜੈਕਟ, ਸਮੱਗਰੀ ਖਪਤ ਅਤੇ ਚੇਂਜਓਵਰ ਸਮਾਂ ਵੇਖੋ।',
      '"PDF" ਜਾਂ "Export CSV" ਨਾਲ ਐਕਸਪੋਰਟ ਕਰੋ। "ਸਮੱਸਿਆ ਰਿਪੋਰਟ" ਹਰ ਮਸ਼ੀਨ ਦਾ ਮਹੀਨਾਵਾਰ ਵਿਸ਼ਲੇਸ਼ਣ ਦਿੰਦੀ ਹੈ।',
    ] },
    settings: { title: 'ਸੈਟਿੰਗਾਂ', steps: [
      'ਸ਼ਿਫਟ ਸਮਾਂ: ਹਰ ਸ਼ਿਫਟ ਦਾ ਸ਼ੁਰੂ/ਖਤਮ ਸਮਾਂ ਅਤੇ ਵਿਕਲਪਿਕ ਬ੍ਰੇਕ ਸਮਾਂ ਸੈੱਟ ਕਰੋ।',
      'ਖੇਤਰੀ: ਟਾਈਮਜ਼ੋਨ, ਦੇਸ਼, ਮੁਦਰਾ ਅਤੇ ਪ੍ਰਤੀ ਘੰਟਾ ਡਾਊਨਟਾਈਮ ਲਾਗਤ।',
      'ਸ਼ਾਪ-ਫਲੋਰ ਪਸੰਦ, ਆਟੋ-ਰੀਆਰਡਰ ਅਤੇ ਤੁਹਾਡੀਆਂ ਸੂਚਨਾਵਾਂ ਵੀ ਇੱਥੇ ਹਨ।',
    ] },
    users: { title: 'ਯੂਜ਼ਰ ਪ੍ਰਬੰਧਨ (ਐਡਮਿਨ)', steps: [
      'ਨਵੇਂ "Pending" ਯੂਜ਼ਰ ਨੂੰ ਭੂਮਿਕਾ ਦੇ ਕੇ ਮਨਜ਼ੂਰੀ ਦਿਓ: ਓਪਰੇਟਰ, ਸੁਪਰਵਾਈਜ਼ਰ ਜਾਂ ਐਡਮਿਨ।',
      'ਹਰ ਯੂਜ਼ਰ ਦੀ ਫੈਕਟਰੀ ਸੈੱਟ ਕਰੋ, ਪਾਸਵਰਡ ਰੀਸੈੱਟ ਕਰੋ ਜਾਂ ਯੂਜ਼ਰ ਹਟਾਓ।',
      'ਦੂਜੀ ਫੈਕਟਰੀ ਸੰਭਾਲਣ ਲਈ ਮੀਨੂ ਵਿੱਚ ਫੈਕਟਰੀ ਸਵਿੱਚਰ ਵਰਤੋ।',
    ] },
    giveAwards: { title: 'ਇਨਾਮ ਦੇਣਾ (ਐਡਮਿਨ)', steps: [
      '"ਇਨਾਮ ਦਿਓ" ਖੋਲ੍ਹੋ, ਓਪਰੇਟਰ ਚੁਣੋ, ਫਿਰ ਟੈਮਪਲੇਟ ਲਓ ਜਾਂ ਆਪਣਾ ਸਿਰਲੇਖ, ਆਈਕਨ ਅਤੇ ਰੰਗ ਬਣਾਓ।',
      'ਓਪਰੇਟਰ ਨੂੰ ਤੁਰੰਤ ਜਸ਼ਨ ਦਿਸਦਾ ਹੈ; ਸੁਪਰਵਾਈਜ਼ਰ ਨੂੰ ਸੂਚਨਾ ਜਾਂਦੀ ਹੈ।',
    ] },
    audit: { title: 'ਆਡਿਟ ਟ੍ਰੇਲ', steps: [
      'ਹਰ ਬਦਲਾਅ (ਕਿਸ ਨੇ, ਕੀ, ਕਦੋਂ) ਦਰਜ ਹੁੰਦਾ ਹੈ। ਜਾਂਚ ਲਈ ਯੂਜ਼ਰ ਜਾਂ ਕਿਸਮ ਨਾਲ ਫ਼ਿਲਟਰ ਕਰੋ।',
    ] },
    faq: { title: 'ਆਮ ਸਵਾਲ', steps: [
      'ਬਟਨ "failed" ਦਿਖਾਵੇ: ਇੰਟਰਨੈੱਟ ਜਾਂਚੋ, ਫਿਰ ਸਾਈਨ ਆਊਟ ਕਰਕੇ ਦੁਬਾਰਾ ਸਾਈਨ ਇਨ ਕਰੋ।',
      'ਮੇਰੀ ਮਸ਼ੀਨ ਨਹੀਂ ਦਿਸ ਰਹੀ: ਸੁਪਰਵਾਈਜ਼ਰ ਤੋਂ ਓਪਰੇਟਰ ਅਸਾਈਨਮੈਂਟ ਵਿੱਚ ਮਸ਼ੀਨ ਨਿਰਧਾਰਤ ਕਰਵਾਓ।',
      'ਸੂਚਨਾਵਾਂ ਨਹੀਂ ਆ ਰਹੀਆਂ: ਸੂਚਨਾਵਾਂ ਖੋਲ੍ਹੋ, ਬੰਦ ਕਰਕੇ ਫਿਰ ਚਾਲੂ ਕਰੋ ਅਤੇ "ਟੈਸਟ ਸੂਚਨਾ ਭੇਜੋ" ਦਬਾਓ।',
      'ਇਹ ਗਾਈਡ ਫਿਰ ਚਾਹੀਦੀ ਹੈ? ਮੀਨੂ ਤੋਂ ਕਦੇ ਵੀ "ਮਦਦ ਅਤੇ ਗਾਈਡ" ਖੋਲ੍ਹੋ।',
    ] },
  },
};

// Welcome walkthrough slides (role-aware): each references a guide section.
export const TOUR = {
  OPERATOR: ['start', 'terminal', 'problems', 'material', 'notify'],
  SUPERVISOR: ['start', 'live', 'scheduling', 'reports', 'notify'],
  ADMIN: ['start', 'users', 'live', 'reports', 'notify'],
};

export const sectionsFor = (role) => SECTIONS.filter((s) => s.roles.includes(role));
